/** @jsx jsx */

import { Fragment, useState, ReactNode } from 'react';
import { isEmpty, isObject } from 'lodash';

import { useRedux } from '@zapier/toolbox-redux-framework';
import {
  ButtonGroup,
  Colors,
  Grid,
  LegacyButton,
  Margin,
  Text,
} from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

import * as Account from 'app/entities/Account';
import AuthConnections from 'app/developer-v3/components/AuthConnections';
import CodeEditor from 'app/developer-v3/components/CodeEditor';
import HelpBox from 'app/developer-v3/components/HelpBox';
import NonFormaticFieldWrapper from 'app/developer-v3/components/NonFormaticFieldWrapper';
import Link from 'app/common/components/Link';
import KeyValueEditor, {
  KeyValue,
} from 'app/developer-v3/components/KeyValueEditor';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import {
  arrayToCsv,
  csvToArray,
  prettyFormatJsonString,
} from 'app/developer-v3/utils';

import { SETUP_TYPES } from './constants';

import type { InputFieldSchema } from 'app/developer-v3/platformSchema/field';

const Styles = {
  testBottomWrapper: css`
    border-top: 1px solid ${Colors.GrayWarm4};
    padding: 25px 15px;
  `,
};

type Props = {
  appName: string;
  canConnectAuth: boolean;
  inputFields: InputFieldSchema[];
  needsAuthMessage: ReactNode;
  onChangeTestData: (value?: string) => void;
  onClickTryRequest: () => Promise<any>;
  onReconnectedAuth: () => Promise<any>;
  onSelectAuth: (authId: string) => void;
  selectedApi: string;
  selectedAuthId: string;
  testData: string;
};

function Setup(props: Props) {
  const {
    appName,
    canConnectAuth,
    inputFields,
    needsAuthMessage,
    onChangeTestData,
    onReconnectedAuth,
    onSelectAuth,
    selectedApi,
    selectedAuthId,
    testData,
  } = props;

  const [type, setType] = useState(SETUP_TYPES.pretty);

  const { accountId } = useRedux(state => ({
    accountId: Account.selectors.all.currentAccountId(state),
  }));

  const transformKeyValueToObject = (inputData, { key, value }) => {
    const inputField = inputFields.find(f => f.key === key);
    inputData[key] = isObject(value)
      ? JSON.stringify(value)
      : inputField?.list
      ? csvToArray(value.trim())
      : value.trim();
    return inputData;
  };

  const onChangePrettyTestData = inputData => {
    const newTestData = {
      ...JSON.parse(testData),
      inputData: inputData.reduce(transformKeyValueToObject, {}),
    };

    onChangeTestData(prettyFormatJsonString(newTestData));
  };

  const onChooseSetupType = event => {
    setType(event.currentTarget.textContent);
  };

  const authAccount = canConnectAuth ? (
    <AuthConnections
      node={{
        selected_api: selectedApi,
        authentication_id: selectedAuthId,
        authentication_owner_id: accountId,
      }}
      onReconnectedAuth={onReconnectedAuth}
      onSaveAuth={({ authentication_id: authId }) => onSelectAuth(authId)}
      service={{ name: appName }}
    />
  ) : (
    <Text fontWeight={600}>{needsAuthMessage}</Text>
  );

  const isPrettySetup = type === SETUP_TYPES.pretty;

  const prettyInputData = () => {
    try {
      const testDataJson = JSON.parse(testData);
      return inputFields.map(({ key }) => ({
        key,
        value: arrayToCsv(testDataJson.inputData[key] || ''),
        isKeyReadOnly: true,
      }));
    } catch (err) {
      return [];
    }
  };

  return (
    <div>
      <Margin margin="25px 15px">
        {selectedAuthId ? (
          authAccount
        ) : (
          <HelpBox
            borderColor="transparent"
            description={
              <Fragment>
                Connect to your app using the above settings with a personal or
                testing account, and Zapier will use this connection to test API
                calls as you build the integration.{' '}
                <Link
                  href={`${BASE_DOC_URL_VB}/build/test-triggers-actions#steps`}
                  target="_blank"
                >
                  Learn more
                </Link>
                .<Margin margin="15px 0px 0px 0px">{authAccount}</Margin>
              </Fragment>
            }
            heading="Test your Authentication Configuration"
            padding="0px"
          />
        )}
      </Margin>
      {canConnectAuth && !isEmpty(inputFields) && (
        <div css={Styles.testBottomWrapper}>
          <NonFormaticFieldWrapper
            helpText={
              <Fragment>
                Add test data for each input field. Comma-separate values to
                create an array.{' '}
                <Link
                  href={`${BASE_DOC_URL_VB}/build/test-triggers-actions`}
                  target="_blank"
                >
                  Learn more
                </Link>
                .{' '}
              </Fragment>
            }
            required={true}
            title="Configure Test Data"
          >
            {({ fieldLabel }) => (
              <Fragment>
                <Grid columns="20% 40% 40%" gap={0}>
                  {fieldLabel}
                  <ButtonGroup
                    columnSize="minmax(75px, max-content)"
                    justify="end"
                  >
                    <LegacyButton
                      color="primary"
                      isSelected={isPrettySetup}
                      onClick={onChooseSetupType}
                      size="compact"
                    >
                      {SETUP_TYPES.pretty}
                    </LegacyButton>
                    <LegacyButton
                      color="secondary"
                      isSelected={!isPrettySetup}
                      onClick={onChooseSetupType}
                      size="compact"
                    >
                      {SETUP_TYPES.raw}
                    </LegacyButton>
                  </ButtonGroup>
                </Grid>
                <Margin margin="25px 0px 0px 0px">
                  {isPrettySetup ? (
                    <KeyValueEditor
                      canChangeItemCount={false}
                      getValuePlaceholder={(item: KeyValue) =>
                        `Test value for {{bundle.inputData.${item.key}}}`
                      }
                      initialItems={prettyInputData()}
                      onUpdate={onChangePrettyTestData}
                    />
                  ) : (
                    <CodeEditor
                      mode="javascript"
                      onChange={onChangeTestData}
                      value={testData}
                    />
                  )}
                </Margin>
              </Fragment>
            )}
          </NonFormaticFieldWrapper>
        </div>
      )}
    </div>
  );
}

Setup.defaultProps = {
  inputFields: [],
};

export default Setup;
